import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";

import useEntity from "../../hooks/useEntity";
import useLocalization from "../../hooks/useLocalization";
import useData from "../../hooks/useData";
import useBrowser from "../../hooks/useBrowser";
import useProject from "../../hooks/useProject";
import { getId, idEquals } from "../../utils/idHelpers";

import Page from "./Page";
import Card from "../layout/Card";
import ProjectEditor from "../editors/ProjectEditor";
import Reps from "../ui/Reps";
import Territories from "../ui/Territories";
import Surveys from "../ui/Surveys";
import ProjectLocations from "../ui/ProjectLocations";

const Project = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getTitle, getLabel } = useLocalization();
  const { getFromServer } = useData();
  const { windowHeight } = useBrowser();
  const { getProjectBanners } = useProject();
  const currentUserRole = useSelector((state) => state.authentication.role);
  const isSysAdmin = currentUserRole === "sysadmin";

  const [projectId, setProjectId] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [client, setClient] = useState(null);
  const [isSyndicated, setIsSyndicated] = useState(false);
  const [backDestination, setBackDestination] = useState(null);

  const {
    setEntity,
    getEntityData,
    entityData,
    setEntityData,
    saveEntityData,
    message,
    updateField,
    deleteEntity,
    deleted,
    emptyObject,
    saved,
  } = useEntity();

  useEffect(() => {
    setEntity("Project");
    setProjectId(location.state.projectId);
    setClientId(location.state.clientId);

    if (location.state.backDestination) {
      setBackDestination(location.state.backDestination);
    }
  }, []);

  useEffect(() => {
    getData();
  }, [projectId]);

  useEffect(() => {
    if (!emptyObject) return;
    if (projectId) return;

    setEntityData({ ...emptyObject, clientId });
  }, [emptyObject]);

  useEffect(() => {
    if (!entityData) return;

    if (entityData.syndicationId) {
      setIsSyndicated(true);
    } else {
      setIsSyndicated(false);
    }
  }, [entityData]);

  useEffect(() => {
    if (!saved) return;

    getData();
  }, [saved]);

  const getData = async () => {
    if (projectId) {
      await getEntityData(projectId);
    }
    if (clientId) {
      const clientData = await getFromServer({
        entityName: "Client",
        id: clientId,
      });
      if (clientData) {
        setClient(clientData);
      }
    }
  };

  const handleToolbarClick = async (name) => {
    switch (name) {
      case "back":
        if (backDestination) {
          navigate(backDestination);
        }
        break;

      case "client":
        navigate("/client", { state: { clientId: entityData.clientId } });
        break;

      case "importExport":
        navigate("/projectimportexport", {
          state: {
            projectId: projectId,
            clientId: clientId,
            name: entityData.name,
          },
        });
        break;

      case "schedule":
        navigate("/projectschedule", {
          state: {
            projectId: projectId,
            clientId: clientId,
          },
        });
        break;

      case "documents":
        navigate("/documents", {
          state: {
            scope: "project",
            id: projectId,
            name: entityData.name,
            returnUrl: "/project",
            returnState: { projectId: projectId, clientId: clientId },
          },
        });
        break;

      case "callList":
        navigate("/calllist", {
          state: {
            projectId,
            clientId,
          },
        });
        break;

      case "projectRefresh":
        if (!isSysAdmin) return;
        navigate("/projectrefresh", {
          state: {
            projectId,
            clientId,
          },
        });
        break;

      case "refresh":
        await getData();
        break;
    }
  };

  const handleSave = async () => {
    await getData();
  };

  useEffect(() => {
    if (!deleted) return;
    navigate("/client", { state: { clientId } });
  }, [deleted]);

  const handleTerritoryUpdate = async () => {
    await getData();
  };

  const handleSurveySave = async (survey) => {
    let newSurveys = [];
    const surveyId = getId(survey);

    if (surveyId) {
      for (const item of entityData.surveys) {
        if (idEquals(item, surveyId)) {
          newSurveys.push({ ...survey });
        } else {
          newSurveys.push({ ...item });
        }
      }
    } else {
      if (Array.isArray(entityData.surveys)) {
        newSurveys = [...entityData.surveys, survey];
      } else {
        newSurveys.push(survey);
      }
    }

    const newData = { ...entityData, surveys: [...newSurveys] };

    await saveEntityData(newData);
  };

  const handleSurveyDelete = async () => {};

  if (!entityData) return null;

  return (
    <Page
      pageName={client?.name ? client.name : "project"}
      entityName={entityData.name}
      includeToolbar
      items={[
        {
          name: "back",
          iconName: "ArrowCircleLeft",
          hide: backDestination ? false : true,
        },
        { name: "client", iconName: "Person" },
        { name: "importExport", iconName: "SwapHoriz" },
        { name: "schedule", iconName: "CalendarMonth" },
        { name: "callList", iconName: "ViewList" },
        {
          name: "projectRefresh",
          iconName: "CloudSync",
          hide: !isSysAdmin,
        },
        { name: "documents", iconName: "Folder" },
        { name: "refresh", iconName: "Refresh" },
      ]}
      onToolItemClick={handleToolbarClick}
    >
      <Box
        display="flex"
        gap={1}
      >
        <Card
          title={getTitle("projectInformation")}
          sx={{ flex: 1, height: `${windowHeight - 140}px` }}
        >
          <ProjectEditor
            projectId={projectId}
            clientId={clientId}
            onSave={handleSave}
          />
        </Card>

        {!isSyndicated ? (
          <Card
            title={getTitle("territories")}
            sx={{ flex: 1, height: `${windowHeight - 140}px` }}
          >
            <Territories
              projectId={projectId}
              users={[]}
              onSave={handleTerritoryUpdate}
              onRefresh={getData}
            />
          </Card>
        ) : null}

        <Card
          title={getTitle("surveys")}
          sx={{ flex: 1, height: `${windowHeight - 140}px` }}
        >
          <Surveys
            projectId={entityData.id}
            clientId={entityData.clientId}
            onSave={handleSurveySave}
          />
        </Card>

        <Card
          title={getTitle("locations")}
          sx={{ flex: 2, height: `${windowHeight - 140}px` }}
        >
          <ProjectLocations
            projectId={entityData.id}
            onRefresh={getData}
          />
        </Card>
      </Box>
    </Page>
  );
};

export default Project;
